(() => {
  if (!document.documentElement.lang.toLowerCase().startsWith('es')) return;
  const root = (window.Shopify?.routes?.root || '/es/').replace(/\/$/, '');
  const script = document.currentScript;
  const surfaceSelector = '.appio-reviews-widget, .appio-detail-review-box';
  let productTitles = {};
  const widgets = new WeakSet();
  const reviewStates = new WeakMap();
  const translations = new Map();
  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const keyFor = (title, body) => JSON.stringify([normalize(title), normalize(body)]);
  const resolve = (title, body) => {
    const entry = translations.get(keyFor(title, body));
    if (!entry) return { title, body, translated: false };
    return {
      ...entry,
      translated: keyFor(entry.title, entry.body) !== keyFor(entry.sourceTitle, entry.sourceBody),
    };
  };
  const getReview = (holder) => {
    const state = reviewStates.get(holder);
    return state?.review || resolve(
      holder.querySelector('.appio-review-item-title')?.textContent?.trim(),
      holder.querySelector('.appio-review-item-body')?.textContent?.trim(),
    );
  };
  const service = { loaded: false, resolve, getReview, ready: null };
  window.YXReviewTranslations = service;

  const localizeReview = (holder) => {
    const title = holder.querySelector('.appio-review-item-title');
    const body = holder.querySelector('.appio-review-item-body');
    if (!body) return;
    const previous = reviewStates.get(holder);
    if (previous?.body === body && previous?.title === title) {
      const expected = previous.original ? previous.review.sourceBody : previous.review.body;
      if (normalize(body.textContent) === normalize(expected)) return;
      holder.querySelector('.yx-review-translation-control')?.remove();
      reviewStates.delete(holder);
    }
    const review = resolve(title?.textContent?.trim(), body.textContent.trim());
    if (!review.translated) return;
    const control = document.createElement('div');
    control.className = 'yx-review-translation-control';
    const note = document.createElement('span');
    note.textContent = 'Traducción del inglés';
    const button = document.createElement('button');
    button.type = 'button';
    const state = { title, body, review, original: false };
    const render = () => {
      if (title) {
        title.textContent = state.original ? review.sourceTitle : review.title;
        title.lang = state.original ? 'en' : 'es';
      }
      body.textContent = state.original ? review.sourceBody : review.body;
      body.lang = state.original ? 'en' : 'es';
      note.textContent = state.original ? 'Original en inglés' : 'Traducción del inglés';
      button.textContent = state.original ? 'Ver traducción' : 'Ver original';
      button.setAttribute('aria-pressed', String(state.original));
    };
    reviewStates.set(holder, state);
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      state.original = !state.original;
      render();
    });
    control.append(note, button);
    body.after(control);
    render();
  };

  const localize = (widget) => {
    widget.querySelectorAll('.appio-review-product-title a[href]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname.startsWith('/products/')) {
        url.pathname = root + url.pathname;
        link.setAttribute('href', url.pathname + url.search + url.hash);
      }
      const handle = url.pathname.split('/products/')[1];
      const translation = productTitles[handle];
      if (translation && link.textContent.trim() === translation.source) link.textContent = translation.es;
    });
    if (widget.matches('.appio-detail-review-box')) localizeReview(widget);
    widget.querySelectorAll('.appio-review-item-holder').forEach(localizeReview);
    widget.querySelectorAll('.appio-review-item-date[data-date]').forEach((date) => {
      const source = date.dataset.date;
      if (!source) return;
      const value = new Date(source);
      if (!Number.isFinite(value.getTime())) return;
      const label = new Intl.DateTimeFormat('es', {
        day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
      }).format(value);
      if (date.textContent !== label) date.textContent = label;
    });
  };

  const attach = () => {
    document.querySelectorAll(surfaceSelector).forEach((widget) => {
      if (widgets.has(widget)) return;
      widgets.add(widget);
      const close = widget.matches('.appio-detail-review-box') && widget.querySelector('.appio-close-button');
      if (close) {
        close.setAttribute('aria-label', 'Cerrar la reseña');
        close.setAttribute('role', 'button');
        close.tabIndex = 0;
        close.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            close.click();
          }
        });
      }
      localize(widget);
      new MutationObserver(() => localize(widget)).observe(widget, { childList: true, subtree: true });
    });
  };
  attach();
  // Observe only Appio mounts; translating a text node never schedules a new mount.
  const mountObserver = new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes].some((node) =>
      node.nodeType === 1 && (node.matches(surfaceSelector) || node.querySelector(surfaceSelector))
    ))) attach();
  });
  mountObserver.observe(document.body, { childList: true, subtree: true });
  const loadJson = async (url) => {
    if (!url) return null;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Review translations unavailable');
      return await response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };
  service.ready = loadJson(script?.dataset.reviewsUrl).then((entries) => {
    (entries || []).forEach((entry) => {
      if (typeof entry.sourceBody !== 'string' || typeof entry.body !== 'string') return;
      translations.set(keyFor(entry.sourceTitle, entry.sourceBody), entry);
    });
  }).catch((error) => {
    console.warn('[YURINOX] Review translations:', error);
  }).then(() => {
    service.loaded = true;
    document.querySelectorAll(surfaceSelector).forEach(localize);
  });
  loadJson(script?.dataset.titlesUrl).then((data) => {
    productTitles = data || {};
    document.querySelectorAll(surfaceSelector).forEach(localize);
  }).catch((error) => console.warn('[YURINOX] Review product labels:', error));
})();
